// frontend/src/components/DocumentViewer.jsx
import React, { useState } from 'react';
import { 
  Dialog, DialogContent, IconButton, Box, Typography, 
  useTheme, useMediaQuery, Button, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// Импорты для рендеринга PDF
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// ВМЕСТО ЭТОГО:
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// ИСПОЛЬЗУЙ ЭТО (надежный локальный импорт):
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs', // 🔥 Изменили .js на .mjs
//   import.meta.url,
// ).toString();
// pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.js`;
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;
// pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const DocumentViewer = ({ open, onClose, file }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Стейты для PDF
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  if (!file || !file.url) return null;

  const fullFileUrl = file.url;

  // Проверяем тип файла
  const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(file.url);
  const isPdf = /\.pdf$/i.test(file.url);
  const contentHeight = fullScreen ? 'calc(100vh - 64px)' : '70vh';

  // Обработчики для PDF
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen={fullScreen}
      maxWidth="lg" 
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      {/* HEADER */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2, 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        height: '64px'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', mr: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name || 'Просмотр документа'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} color="inherit" size="large" edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ 
        p: 0, 
        bgcolor: 'grey.900', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: contentHeight, 
        overflow: 'hidden',
        position: 'relative'
      }}>
        
        {/* ИЗОБРАЖЕНИЯ */}
        {isImage && (
          <TransformWrapper>
            {() => (
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
                <img 
                  src={fullFileUrl} 
                  alt={file.name} 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: contentHeight,
                    objectFit: 'contain' 
                  }} 
                />
              </TransformComponent>
            )}
          </TransformWrapper>
        )}

        {/* PDF ДОКУМЕНТЫ */}
        {isPdf && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <TransformWrapper>
                {() => (
                  <TransformComponent wrapperStyle={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Document
                      file={fullFileUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={<CircularProgress color="primary" />}
                      error={<Typography color="error">Ошибка загрузки PDF.</Typography>}
                    >
                      <Page 
                        pageNumber={pageNumber} 
                        renderTextLayer={false} 
                        renderAnnotationLayer={false}
                        // Адаптивная ширина под экран мобильного устройства
                        width={fullScreen ? window.innerWidth - 16 : null} 
                      />
                    </Document>
                  </TransformComponent>
                )}
              </TransformWrapper>
            </Box>

            {/* Панель управления PDF (Пагинация) */}
            {numPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                bgcolor: 'grey.800', 
                p: 1,
                gap: 2
              }}>
                <Button 
                  variant="contained" 
                  size="small"
                  disabled={pageNumber <= 1} 
                  onClick={() => changePage(-1)}
                  startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                >
                  Пред
                </Button>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {pageNumber} из {numPages}
                </Typography>
                <Button 
                  variant="contained" 
                  size="small"
                  disabled={pageNumber >= numPages} 
                  onClick={() => changePage(1)}
                  endIcon={<ArrowForwardIosIcon fontSize="small" />}
                >
                  След
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* НЕИЗВЕСТНЫЙ ФОРМАТ */}
        {!isImage && !isPdf && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" mb={2}>
              Формат файла не поддерживается для предпросмотра.
            </Typography>
            <Button variant="contained" href={fullFileUrl} target="_blank" rel="noopener noreferrer">
              Скачать документ
            </Button>
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;